import React from 'react';
import { Task, FullProject } from '../../../types';
import { areProjectIdsMatching, isMatchingTaskId } from '../../../utils/idNormalizer';
import { prepareTaskMutationUpdates } from '../../../domain/applyTaskMutation';
import { applyProjectWideTaskUpdateAndContactSync } from './taskContactSyncUtils';
import { ProjectSyncTracker } from '../sync/projectSyncStateTracker';

const SILENT_MUTATION_FIELDS: ReadonlyArray<keyof Task> = [
  'startTime',
  'endTime',
  'notes',
  'lastContactDate',
  'activeEditor',
];

export interface TaskMutationBatchItem {
  id: string | number;
  updates: Partial<Task>;
  projectId?: string;
}

export interface ExecuteTaskMutationBatchParams {
  batch: TaskMutationBatchItem[];
  allProjects: FullProject[];
  fallbackTasks: Task[];
  currentProjectId: string;
  COMM_PROJECT_ID: string;
  INBOX_PROJECT_ID: string;
  userEmail?: string;
  normalizeProjectId: (id?: string) => string | undefined;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
}

export interface RecurringTaskToSpawn {
  orig: Task;
  finalUpdates: Partial<Task>;
  projectId: string;
}

export interface ExecuteTaskMutationBatchResult {
  appliedCount: number;
  updatedProjects: FullProject[];
  mutatedTasksForRawSync: Task[];
  recurringToSpawn: RecurringTaskToSpawn[];
  hasMilestoneCelebration: boolean;
  hasMajorChanges: boolean;
  singleTaskLogDescription?: string;
}

/**
 * Resolves `(targetProject, originalTask)` across `projects` (with fallback to `fallbackTasks`).
 */
export function resolveTargetProjectAndTask(
  id: string | number,
  normalizedTargetId: string | undefined,
  projects: FullProject[],
  fallbackTasks: Task[],
  currentProjectId: string
): { targetProject?: FullProject; originalTask?: Task } {
  const safeIdStr = String(id);

  if (normalizedTargetId) {
    for (const p of projects) {
      if (!areProjectIdsMatching(p.id, normalizedTargetId)) continue;
      const found = p.tasks.find(t => isMatchingTaskId(id, t.id, p.id) || String(t.id) === safeIdStr);
      if (found) {
        return { targetProject: p, originalTask: found };
      }
    }
  }

  for (const p of projects) {
    const found = p.tasks.find(t => isMatchingTaskId(id, t.id, p.id) || String(t.id) === safeIdStr);
    if (found) {
      return { targetProject: p, originalTask: found };
    }
  }

  const foundInCurrentTasks = fallbackTasks.find(
    t => isMatchingTaskId(id, t.id, currentProjectId) || String(t.id) === safeIdStr
  );
  if (foundInCurrentTasks) {
    const targetProject =
      projects.find(p => areProjectIdsMatching(p.id, currentProjectId)) || projects[0];
    return { targetProject, originalTask: foundInCurrentTasks };
  }

  return {};
}

/**
 * Phase 2 · Schritt 2.5: Unified mutation pipeline shared by both `updateTask` (single item)
 * and `updateTasksMultiple` (multi-item batch).
 * Guarantees that every task update—whether single or batch—executes:
 * - `prepareTaskMutationUpdates` (status/completed sync, timer stop, history, recurrence check)
 * - `applyProjectWideTaskUpdateAndContactSync` (contact creation/sync in `COMM_PROJECT_ID`,
 *   contact rename propagation, `lastContactDate` sync, dependency date shifts, structural sharing)
 */
export function executeTaskMutationBatch({
  batch,
  allProjects,
  fallbackTasks,
  currentProjectId,
  COMM_PROJECT_ID,
  INBOX_PROJECT_ID,
  userEmail,
  normalizeProjectId,
  markProjectDirty,
  onMarkTaskDirty,
}: ExecuteTaskMutationBatchParams): ExecuteTaskMutationBatchResult {
  let workingProjects = allProjects;
  let appliedCount = 0;
  let hasMilestoneCelebration = false;
  let hasMajorChanges = false;
  let singleTaskLogDescription: string | undefined;
  const mutatedTasksMap = new Map<string, Task>();
  const recurringToSpawn: RecurringTaskToSpawn[] = [];

  for (const item of batch) {
    const rawTargetProjectId =
      item.projectId === 'inbox_inforum'
        ? INBOX_PROJECT_ID
        : item.projectId === 'comm_internal'
          ? COMM_PROJECT_ID
          : item.projectId;
    const normalizedTargetId = normalizeProjectId(rawTargetProjectId);

    const { targetProject, originalTask } = resolveTargetProjectAndTask(
      item.id,
      normalizedTargetId,
      workingProjects,
      fallbackTasks,
      currentProjectId
    );

    if (!targetProject || !originalTask) {
      if (batch.length === 1) {
        console.warn(`[ProjectManager] updateTask failed: Task ${item.id} not found.`);
      }
      continue;
    }

    const hasChanges = Object.entries(item.updates).some(([key, value]) => {
      const orig = originalTask[key as keyof Task];
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(orig) !== JSON.stringify(value);
      }
      return orig !== value;
    });
    if (!hasChanges) continue;

    appliedCount++;

    const {
      finalUpdates,
      shouldSpawnRecurrence,
      isMilestoneCelebration,
    } = prepareTaskMutationUpdates(originalTask, item.updates, {
      userEmail,
      customColumns: targetProject.metadata?.customColumns,
    });

    const majorChanges = Object.keys(item.updates).filter(
      k => !SILENT_MUTATION_FIELDS.includes(k as keyof Task)
    );
    if (majorChanges.length > 0) {
      hasMajorChanges = true;
      if (batch.length === 1) {
        let desc = `Aufgabe "${originalTask.name}" aktualisiert`;
        if (finalUpdates.name) {
          desc = `Aufgabe umbenannt zu "${finalUpdates.name}"`;
        } else if (finalUpdates.completed !== undefined) {
          desc = `Aufgabe "${originalTask.name}" als ${finalUpdates.completed ? 'erledigt' : 'offen'} markiert`;
        }
        singleTaskLogDescription = desc;
      }
    }

    if (isMilestoneCelebration && (batch.length > 1 || majorChanges.length > 0)) {
      hasMilestoneCelebration = true;
    }

    if (shouldSpawnRecurrence && (batch.length > 1 || majorChanges.length > 0)) {
      recurringToSpawn.push({
        orig: originalTask,
        finalUpdates,
        projectId: originalTask.projectId || item.projectId || targetProject.id,
      });
    }

    workingProjects = applyProjectWideTaskUpdateAndContactSync({
      id: item.id,
      originalTask,
      finalUpdates,
      targetProject,
      allProjects: workingProjects,
      COMM_PROJECT_ID,
      markProjectDirty,
      onMarkTaskDirty,
      onContactCreated: (newContact) => {
        mutatedTasksMap.set(String(newContact.id), newContact);
      },
      onTaskMutated: (mutatedTask) => {
        mutatedTasksMap.set(String(mutatedTask.id), mutatedTask);
      },
    });
  }

  return {
    appliedCount,
    updatedProjects: workingProjects,
    mutatedTasksForRawSync: Array.from(mutatedTasksMap.values()),
    recurringToSpawn,
    hasMilestoneCelebration,
    hasMajorChanges,
    singleTaskLogDescription,
  };
}

export interface CommitTaskListUpdateParams {
  newTasks: Task[];
  currentProjectId: string;
  tracker: ProjectSyncTracker;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  updateCurrentProjectTasks: (currentProj: FullProject, updatedProjList: FullProject[]) => void;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
}

function areDependenciesEqual(
  depsA?: (string | number)[],
  depsB?: (string | number)[]
): boolean {
  const a = depsA || [];
  const b = depsB || [];
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (String(a[i]) !== String(b[i])) return false;
  }
  return true;
}

/**
 * Safely commits a task list update across either a single project (`GanttView`, `DependencyModal`,
 * `sortTasksByDate`) or multiple projects simultaneously (`GlobalGanttView`), ensuring:
 * 1. Cross-project task lists never overwrite `currentProjectId` with foreign tasks.
 * 2. Display-only overrides from `GlobalGanttView` (`displayName`, single-date fallbacks) do not
 *    corrupt unmodified task titles or wipe filtered-out tasks in the source projects.
 * 3. Only tasks that actually changed (or changed order) are marked dirty in `dirtyTaskIdsRef`.
 */
export function commitTaskListUpdate({
  newTasks,
  currentProjectId,
  tracker,
  setAllProjects,
  setTasks,
  updateCurrentProjectTasks,
  markProjectDirty,
  onMarkTaskDirty,
}: CommitTaskListUpdateParams): void {
  const currentAllProjects = tracker.getAllProjects();
  const distinctProjectIds = new Set<string>();
  newTasks.forEach((t) => {
    if (t.projectId) distinctProjectIds.add(t.projectId);
  });

  const isMultiProjectCommit =
    distinctProjectIds.size > 1 ||
    (distinctProjectIds.size === 1 &&
      Boolean(
        currentProjectId &&
          !Array.from(distinctProjectIds).some((pid) =>
            areProjectIdsMatching(pid, currentProjectId)
          )
      ));

  const now = Date.now();

  if (isMultiProjectCommit) {
    const incomingById = new Map<string, Task>();
    newTasks.forEach((nt) => incomingById.set(String(nt.id).trim(), nt));

    const modifiedProjectIds = new Set<string>();
    const mutatedTasksForRawSync: Task[] = [];

    const updatedProjects = currentAllProjects.map((p) => {
      let projectChanged = false;
      const nextProjTasks = (p.tasks || []).map((oldT) => {
        const idStr = String(oldT.id).trim();
        const incoming =
          incomingById.get(idStr) ||
          newTasks.find((nt) => isMatchingTaskId(nt.id, oldT.id, p.id));
        if (!incoming) return oldT;

        const effectiveOldStart = oldT.start || oldT.end || '';
        const effectiveOldEnd = oldT.end || oldT.start || '';
        const startChanged = (incoming.start || '') !== effectiveOldStart && (incoming.start || '') !== (oldT.start || '');
        const endChanged = (incoming.end || '') !== effectiveOldEnd && (incoming.end || '') !== (oldT.end || '');
        const depsChanged = !areDependenciesEqual(incoming.dependencies, oldT.dependencies);
        const sortChanged =
          incoming.sortOrder !== undefined && incoming.sortOrder !== oldT.sortOrder;

        if (!startChanged && !endChanged && !depsChanged && !sortChanged) {
          return oldT;
        }

        projectChanged = true;
        const stamped: Task = {
          ...oldT,
          ...(startChanged ? { start: incoming.start } : {}),
          ...(endChanged ? { end: incoming.end } : {}),
          ...(depsChanged ? { dependencies: incoming.dependencies } : {}),
          ...(sortChanged ? { sortOrder: incoming.sortOrder } : {}),
          updatedAt: Math.max(incoming.updatedAt || 0, now),
        };
        mutatedTasksForRawSync.push(stamped);
        onMarkTaskDirty(stamped.id, p.id);
        return stamped;
      });

      if (!projectChanged) return p;
      modifiedProjectIds.add(p.id);
      markProjectDirty(p.id);
      return { ...p, tasks: nextProjTasks, lastModified: now };
    });

    if (modifiedProjectIds.size > 0) {
      tracker.syncMultipleTasks(mutatedTasksForRawSync, true);
      tracker.commitProjects(updatedProjects, setAllProjects, modifiedProjectIds);
      const currentInNew = updatedProjects.find((p) =>
        areProjectIdsMatching(p.id, currentProjectId)
      );
      if (currentInNew) {
        updateCurrentProjectTasks(currentInNew, updatedProjects);
      }
    }
    return;
  }

  // Single-project mode (all tasks belong to currentProjectId)
  const targetProject = currentAllProjects.find((p) =>
    areProjectIdsMatching(p.id, currentProjectId)
  );
  const oldTaskMap = new Map<string, { task: Task; index: number }>();
  (targetProject?.tasks || []).forEach((t, index) => {
    oldTaskMap.set(String(t.id).trim(), { task: t, index });
  });

  const orderChanged =
    Boolean(targetProject) &&
    (targetProject!.tasks.length !== newTasks.length ||
      newTasks.some((nt, idx) => oldTaskMap.get(String(nt.id).trim())?.index !== idx));

  const nextTasks = newTasks.map((nt, idx) => {
    const idStr = String(nt.id).trim();
    const oldEntry = oldTaskMap.get(idStr);
    const oldT = oldEntry?.task;

    let isTaskModified = !oldT;
    if (oldT && oldT !== nt) {
      isTaskModified =
        oldT.start !== nt.start ||
        oldT.end !== nt.end ||
        oldT.name !== nt.name ||
        oldT.status !== nt.status ||
        oldT.completed !== nt.completed ||
        oldT.sortOrder !== nt.sortOrder ||
        oldT.parentId !== nt.parentId ||
        !areDependenciesEqual(oldT.dependencies, nt.dependencies);
    }
    if (!isTaskModified && orderChanged && oldEntry && oldEntry.index !== idx) {
      isTaskModified = true;
    }

    if (isTaskModified) {
      const nextSortOrder =
        orderChanged && oldEntry && oldEntry.index !== idx && nt.sortOrder === oldT?.sortOrder
          ? (idx + 1) * 1000
          : nt.sortOrder;
      const stamped: Task = {
        ...nt,
        ...(nextSortOrder !== undefined ? { sortOrder: nextSortOrder } : {}),
        updatedAt: Math.max(nt.updatedAt || 0, now),
      };
      onMarkTaskDirty(stamped.id, currentProjectId);
      return stamped;
    }
    return nt;
  });

  setTasks(nextTasks);

  const updatedProjects = currentAllProjects.map((p) => {
    if (areProjectIdsMatching(p.id, currentProjectId)) {
      return { ...p, tasks: nextTasks, lastModified: now };
    }
    return p;
  });

  tracker.commitProjects(updatedProjects, setAllProjects, [currentProjectId]);
  tracker.replaceProjectTasks(currentProjectId, nextTasks);
  markProjectDirty(currentProjectId);
}
